import React, { ReactElement, useEffect } from 'react';
import { useAppState } from '@unfold/provider';
import { Logo } from '@unfold/logo';
import { Menu } from '@unfold/menu';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteProps,
} from 'react-router-dom';
import {
  AppContainer,
  Content,
  ContentBody,
  ContentTop,
  LogoutLink,
  LogoWrapper,
  MenuContainer,
  MenuWrapper,
} from './styled';
import { routes } from './routes';
import queryString from 'query-string';

interface IRouteProps {
  authenticated: boolean;
};

function LoginRoute({ authenticated }: IRouteProps): ReactElement {
  return (
    <Route
      path={routes.login.path}
      render={(routeProps: RouteProps): ReactElement => {
        if (authenticated) {
          return <Redirect to="/" />;
        }

        return <routes.login.component {...routeProps} />;
      }}
    />
  );
};

function HomeRoute({ authenticated }: IRouteProps): ReactElement {
  const [, actions] = useAppState();
  return (
    <Route
      path="/"
      render={(routeProps: RouteProps): ReactElement => {
        if (authenticated) {
          return <Redirect to="/brand" />
        }

        const backToLogin = (): ReactElement => <Redirect to="/login" />;

        const fragment = new URLSearchParams(routeProps.location?.hash.substring(1));
        const queryParams = new URLSearchParams(routeProps.location?.search);
        let token;

        if (fragment.size > 0) {
          token = fragment.get('access_token');
        } else {
          token = queryParams.get('jwt');
        }

        if (!token) {
          return backToLogin();
        }

        if (fragment.size > 0) {
          const tokenType = fragment.get('token_type');
          if (tokenType !== 'bearer') {
            return backToLogin();
          }
        }

        actions.addToken(token as string);

        return <Redirect to="/" />
      }}
    />
  )
};

function AuthenticatedRoute(route: any): ReactElement {
  return (
    <Route
      path={route.path}
      render={(props): ReactElement => {
        if (!localStorage.getItem('token')) {
          return <Redirect to={routes.login.path} />;
        }
        return <route.component {...props} />
      }}
    />
  );
}

export const Main: React.FC = () => {
  const [state, actions] = useAppState();
  useEffect(() => {
    actions.init();
  }, []);

  useEffect(() => {
    state.authenticated && actions.getUser();
  }, [state.authenticated]);

  const handleLogoutClick = (): void => {
    actions.logout();
  };

  if (state.loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <LoginRoute authenticated={state.authenticated} />
      <HomeRoute authenticated={state.authenticated} />
      <AppContainer isHidden={!state.authenticated}>
        <MenuContainer>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>
          <MenuWrapper>
            <Menu />
          </MenuWrapper>
        </MenuContainer>
        <Content>
          <ContentTop>
            {state.user?.name_first} {state.user?.name_last}
            <LogoutLink onClick={handleLogoutClick}>Log out</LogoutLink>
          </ContentTop>
          <ContentBody>
            <Switch>
              <Route exact path={['/', '/home']}>
                <Redirect to="/brand" />
              </Route>
              {routes.requireAuth.map((route, i) => (
                <AuthenticatedRoute key={i} {...route} />
              ))}
            </Switch>
          </ContentBody>
        </Content>
      </AppContainer>

    </Router>
  );
};
